"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, FileText, SlidersHorizontal as Sliders, Link2, Smartphone, Compass, Layers, ChartBar as BarChart3, Zap, Check, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const servicesData = [{
  id: "gbp-opt",
  title: "Google Business Profile Optimization",
  category: "Map Rankings",
  icon: MapPin,
  description: "Claim, verify, and fully optimize your Google Business Profile to capture high-intent local map pack searches.",
  deliverable1: "GBP category & listing audit",
  deliverable2: "Geo-targeted photo & post schedule",
  deliverable3: "Q&A & business info sync",
  linkUrl: "/LocalSEOPage"
}, {
  id: "citations",
  title: "Local Citation Building & Audit",
  category: "NAP Consistency",
  icon: Building2,
  description: "Clean up inconsistent NAP (Name, Address, Phone) data and build high-authority local directory citations.",
  deliverable1: "Inconsistent citation audit",
  deliverable2: "Tier-1 directory submissions",
  deliverable3: "Duplicate listing removal",
  linkUrl: "/LocalSEOPage"
}, {
  id: "review-mgmt",
  title: "Review & Reputation Management",
  category: "Trust & Conversion",
  icon: Star,
  description: "Implement ethical review acquisition workflows to boost your ratings and convert searchers into inquiries.",
  deliverable1: "Automated review funnel setup",
  deliverable2: "Negative feedback alert system",
  deliverable3: "Review response strategy",
  linkUrl: "/LocalSEOPage"
}, {
  id: "local-schema",
  title: "Local Schema & Structured Data",
  category: "Technical SEO",
  icon: Code2,
  description: "Embed customized JSON-LD LocalBusiness schema to help search engines pinpoint your locations and service areas.",
  deliverable1: "JSON-LD LocalBusiness code",
  deliverable2: "Geo-coordinates & area markup",
  deliverable3: "Schema validation & monitoring",
  linkUrl: "/TechnicalSeoPage"
}, {
  id: "geo-content",
  title: "Geo-Targeted Content Strategy",
  category: "Content Strategy",
  icon: FileText,
  description: "Create localized landing pages and articles engineered to rank for location-specific search queries.",
  deliverable1: "Location keyword research",
  deliverable2: "Localized landing page copy",
  deliverable3: "E-E-A-T trust signal integration",
  linkUrl: "/NationalSeoPage"
}, {
  id: "onpage-local",
  title: "On-Page Local SEO Optimization",
  category: "On-Page SEO",
  icon: Sliders,
  description: "Optimize title tags, meta descriptions, headings, and internal links with localized keyword intent.",
  deliverable1: "Title & meta tag optimization",
  deliverable2: "Localized heading hierarchy",
  deliverable3: "Internal location link silo",
  linkUrl: "/FreelanceSeoConsultantPage"
}, {
  id: "local-links",
  title: "Local Link Building & Outreach",
  category: "Authority",
  icon: Link2,
  description: "Earn authoritative backlinks from local news outlets, industry sponsors, and regional business directories.",
  deliverable1: "Local media & sponsor outreach",
  deliverable2: "Unlinked brand mention reclamation",
  deliverable3: "Regional partner backlink strategy",
  linkUrl: "/ServicesPage"
}, {
  id: "mobile-voice",
  title: "Mobile & Voice Search SEO",
  category: "UX & Speed",
  icon: Smartphone,
  description: "Ensure your website loads instantaneously on mobile devices and answers voice queries like 'near me'.",
  deliverable1: "Mobile speed optimization",
  deliverable2: "Conversational FAQ optimization",
  deliverable3: "Click-to-call conversion setup",
  linkUrl: "/TechnicalSeoPage"
}, {
  id: "map-pack",
  title: "Hyper-Local Map Pack Ranking",
  category: "Local Strategy",
  icon: Compass,
  description: "Target micro-neighborhoods and specific postcodes to dominate the Google 3-Pack for high-value services.",
  deliverable1: "Postcode geo-relevance analysis",
  deliverable2: "Proximity signal enhancement",
  deliverable3: "Local map pack rank tracking",
  linkUrl: "/LocalSEOPage"
}, {
  id: "multi-location",
  title: "Multi-Location SEO Management",
  category: "Enterprise Local",
  icon: Layers,
  description: "Scalable local SEO strategies for businesses operating across multiple towns, cities, or regional hubs.",
  deliverable1: "Scalable location page architecture",
  deliverable2: "Multi-GBP bulk management",
  deliverable3: "Regional performance analytics",
  linkUrl: "/InternationalSeoPage"
}, {
  id: "competitor-analysis",
  title: "Competitor Local SEO Analysis",
  category: "Market Research",
  icon: BarChart3,
  description: "Analyze top local competitors to identify their backlink sources, keyword gaps, and map strategy advantages.",
  deliverable1: "Competitor backlink profiling",
  deliverable2: "Category & keyword gap audit",
  deliverable3: "Actionable counter-strategy",
  linkUrl: "/SeoAuditsPage"
}, {
  id: "local-technical",
  title: "Local Technical SEO & Speed Audit",
  category: "Site Health",
  icon: Zap,
  description: "Resolve indexation issues, crawl errors, and Core Web Vitals to provide a fast, frictionless user experience.",
  deliverable1: "Core Web Vitals diagnostic",
  deliverable2: "Crawlability & sitemap repair",
  deliverable3: "Monthly health monitoring",
  linkUrl: "/TechnicalSeoPage"
}];
export default function Servicegrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicegrid" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium bg-secondary text-secondary-foreground">
            Specialized SEO Capabilities
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-4">
            Hands-On SEO Solutions Designed for Sustainable Search Growth
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            When you work with YE Consultancy, you work directly with me—Yassine Essaadi.
            No account managers or outsourced scripts. Just tailored, high-converting SEO executions.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesData.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} data-index={i} variants={itemVariants} className="flex">
                <Card className="bg-card border border-border shadow-sm rounded-xl overflow-hidden flex flex-col justify-between w-full transition-[box-shadow,border-color] duration-300 hover:shadow-lg hover:border-primary/40 group">
                  <div>
                    <div className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md bg-primary-foreground/15 text-primary-foreground">
                          <IconComponent className="size-5" />
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/90">
                          {service.category}
                        </span>
                      </div>
                      <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground bg-primary-foreground/10 text-[11px] uppercase tracking-wide">
                        Direct Service
                      </Badge>
                    </div>

                    <CardHeader className="pt-6 pb-2 px-6">
                      <CardTitle className="text-xl font-bold font-serif text-card-foreground group-hover:text-primary transition-colors">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm mt-2 leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="px-6 py-4">
                      <div className="pt-2 border-t border-border/60">
                        <p className="text-xs font-semibold text-foreground/80 uppercase tracking-wider mb-3">
                          Key Deliverables:
                        </p>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <Check className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{service.deliverable1}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{service.deliverable2}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{service.deliverable3}</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="px-6 pb-6 pt-2">
                    <Button asChild variant="outline" size="default" className="w-full justify-between border-border hover:bg-primary hover:text-primary-foreground transition-colors group/btn">
                      <Link to={service.linkUrl}>
                        <span>Explore Strategy</span>
                        <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-14 md:mt-20 p-8 md:p-10 rounded-2xl bg-muted border border-border flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-2 max-w-2xl">
            <h3 className="text-xl md:text-2xl font-bold font-serif text-foreground">
              Not sure which SEO strategy fits your current growth stage?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              Book a free consultation directly with Yassine to review your website metrics and local competition.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="shadow-md">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}